import { primaryColor } from '../../constants/theme';
import { LoginWrapper } from '../LoginWrapper';
import { MobileLogo } from './SignUpEmail';
import {
  Stack,
  Title,
  TextInput,
  Text,
  Group,
  Button,
  ScrollArea,
  Badge,
  ActionIcon,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { useOnboarding } from './SignUpContext';
import { useNavigate } from 'react-router-dom';
import { PageRoutes } from '../../constants/routes';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../auth/AuthContext';

export const SearchTerm = () => {
  const { form, onSubmit, isError, isSuccess } = useOnboarding();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<Set<string>>(new Set());
  const [inputValue, setInputValue] = useState('');
  const [loadingButton, setLoadingButton] = useState<
    undefined | 'SKIP' | 'NEXT'
  >();
  const { getProfileAndRedirect } = useApp();
  const { accessToken } = useAuth();

  useEffect(() => {
    // TODO if user fully setup go to search page
    if (form.validate().hasErrors) {
      form.clearErrors();
      navigate(PageRoutes.ONBOARDING_YOUR_DETAILS); // TODO could probs remove
    }
  }, []);

  const addSearchTerm = (term: string) => {
    if (term && term.trim() !== '') {
      setSearchTerm((prev) => new Set(prev).add(term.trim()));
    }
    setInputValue('');
  };

  const onSubmitForm = (isSkip: boolean) => {
    form.setFieldValue(
      'searchTerms',
      Array.from(searchTerm).map((term) => ({ searchTerm: term }))
    );
    if (form.validate().hasErrors) return;
    setLoadingButton(isSkip ? 'SKIP' : 'NEXT');
    onSubmit();
  };

  useEffect(() => {
    if (isError) {
      setLoadingButton(undefined);
    }
  }, [isError]);

  useEffect(() => {
    if (isSuccess) {
      getProfileAndRedirect(accessToken as string);
    }
  }, [isSuccess]);

  return (
    <LoginWrapper animate={false}>
      <MobileLogo />
      <Stack>
        <Group justify="space-between">
          <Title order={2} ta="left">
            Search Terms
          </Title>
          <Button
            td="underline"
            variant="transparent"
            onClick={() => onSubmitForm(true)}
            c={'dimmed'}
            loading={loadingButton === 'SKIP'}
            loaderProps={{ color: primaryColor }}
          >
            SKIP
          </Button>
        </Group>
        <Text ta="left">
          Please add search terms that users can use to search for you
          (optional)
        </Text>
        <Group justify="space-around">
          <TextInput
            w="70%"
            placeholder="Enter search term"
            value={inputValue}
            onChange={(e) => setInputValue(e.currentTarget.value)}
          />
          <Button
            w="20%"
            color={primaryColor}
            onClick={() => addSearchTerm(inputValue)}
          >
            Add
          </Button>
        </Group>
        <ScrollArea h="40vh" p="lg">
          <Group gap="xs" wrap="wrap">
            {Array.from(searchTerm).map((term, index) => (
              <OptionItem
                key={index}
                term={term}
                onDismiss={(term) =>
                  setSearchTerm((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(term);
                    return newSet;
                  })
                }
              />
            ))}
          </Group>
        </ScrollArea>
        <Button
          fullWidth
          radius={'lg'}
          size={'md'}
          onClick={() => onSubmitForm(false)}
          loading={loadingButton === 'NEXT'}
          color={primaryColor}
        >
          NEXT
        </Button>
        {isError ||
          (!form.isValid && (
            <Text c="red">Something went wrong, please try again</Text>
          ))}
      </Stack>
    </LoginWrapper>
  );
};

export const OptionItem = ({
  term,
  onDismiss,
}: {
  term: string;
  onDismiss: (term: string) => void;
}) => {
  return (
    <Badge
      size="lg"
      c="black"
      rightSection={
        <ActionIcon
          color="transparent"
          style={{ '--ai-hover': primaryColor }}
          onClick={() => onDismiss(term)}
        >
          <IconX size={20} color="black" />
        </ActionIcon>
      }
      variant="filled"
      color="#e3e3e3"
      radius="sm"
      pr={0}
    >
      {term}
    </Badge>
  );
};
